import type {
  BlockTemplateEnvelope,
  BlockTemplateListResponse,
  CreateBlockTemplateDraftFromRequestBlockInput,
  CreateBlockTemplateFromRequestBlockInput,
  OwnershipTransferInput,
  RequestBlockPatchInput,
  RequestBlockWriteInput,
  ReviewVersionInput,
  SubmitForReviewInput,
  TemplateDeleteResponse,
  TemplateSharesInput,
  TemplateVersionActivityEnvelope,
} from '@api/types/requestFlow';
import API from '@api/API';
import { ENDPOINTS } from '../endpoints';

type Id = string | number;

export const blockTemplatesApi = {
  list: async (): Promise<BlockTemplateListResponse> => {
    const res = await API().get(ENDPOINTS.blockTemplates.list());
    return res.data;
  },

  get: async (templateId: Id): Promise<BlockTemplateEnvelope> => {
    const res = await API().get(ENDPOINTS.blockTemplates.one(templateId));
    return res.data;
  },

  create: async (data: RequestBlockWriteInput): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.create(), data);
    return res.data;
  },

  createDraft: async (templateId: Id): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.draft(templateId));
    return res.data;
  },

  updateVersion: async (
    templateId: Id,
    versionId: Id,
    data: RequestBlockPatchInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().patch(ENDPOINTS.blockTemplates.version(templateId, versionId), data);
    return res.data;
  },

  getVersionActivity: async (
    templateId: Id,
    versionId: Id,
  ): Promise<TemplateVersionActivityEnvelope> => {
    const res = await API().get(ENDPOINTS.blockTemplates.versionActivity(templateId, versionId));
    return res.data;
  },

  submitForReview: async (
    templateId: Id,
    versionId: Id,
    data: SubmitForReviewInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.submit(templateId, versionId), data);
    return res.data;
  },

  reviewVersion: async (
    templateId: Id,
    versionId: Id,
    data: ReviewVersionInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.review(templateId, versionId), data);
    return res.data;
  },

  duplicate: async (templateId: Id): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.duplicate(templateId));
    return res.data;
  },

  delete: async (templateId: Id): Promise<TemplateDeleteResponse> => {
    const res = await API().delete(ENDPOINTS.blockTemplates.delete(templateId));
    return res.data;
  },

  replaceShares: async (
    templateId: Id,
    data: TemplateSharesInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.shares(templateId), data);
    return res.data;
  },

  transferOwnership: async (
    templateId: Id,
    data: OwnershipTransferInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.ownershipTransfer(templateId), data);
    return res.data;
  },

  createFromBlock: async (
    data: CreateBlockTemplateFromRequestBlockInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.fromBlock(), data);
    return res.data;
  },

  createDraftFromBlock: async (
    data: CreateBlockTemplateDraftFromRequestBlockInput,
  ): Promise<BlockTemplateEnvelope> => {
    const res = await API().post(ENDPOINTS.blockTemplates.fromBlockDraft(), data);
    return res.data;
  },
};
